import { parseAsString, parseAsStringEnum, useQueryStates } from 'nuqs';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { AffiliateCsvImportCreatedResponse } from '@fluenceur/types';
import { toaster } from '@/components/ui/toaster';
import { useAffiliateCsvImport } from '@/hooks/actions/use-affiliate-csv-import';
import { useListProgram } from '@/hooks/queries/use-list-program';
import { apiErrorCode, apiErrorMessage, apiValidationFieldError } from '@/libs/api-error';
import useAffiliateCsvImportStore, {
  isTerminalImportStatus,
} from '@/stores/affiliate-csv-import-store';
import type { CsvImportDelivery, ImportBatchProgress } from '@/stores/affiliate-csv-import-store';
import type { ParsedRow } from '../_lib/csv-parser';

export type WizardStep = 'upload' | 'mapping' | 'preview' | 'delivery' | 'importing' | 'done';

export const STEP_VALUES: WizardStep[] = [
  'upload',
  'mapping',
  'preview',
  'delivery',
  'importing',
  'done',
];

/**
 * Pure predicate for the wizard's "redirect to upload when payload is missing" guard.
 *
 * Steps that read parsed rows from the Zustand store (mapping, preview, delivery) need to bounce
 * back to upload if a user lands on them with an empty store (fresh tab on a shareable
 * URL). The `importing` step is intentionally excluded because it can rehydrate from
 * GET /:batchId without rows in the store.
 */
export const shouldRedirectToUpload = (step: WizardStep, rowCount: number): boolean =>
  (step === 'mapping' || step === 'preview' || step === 'delivery') && rowCount === 0;

/** A running import is durable server-side and can safely be observed after modal close. */
export const shouldContinueImportInBackground = (
  step: WizardStep | null,
  programId: string,
  batchId: string,
  batchStatus: ImportBatchProgress['status'] | undefined
): boolean =>
  step === 'importing' &&
  Boolean(programId) &&
  Boolean(batchId) &&
  !isTerminalImportStatus(batchStatus);

/** Initial UI state is only a bridge; terminal counts are rehydrated from the backend. */
export const buildInitialImportBatch = (result: AffiliateCsvImportCreatedResponse) => ({
  batchId: result.batchId,
  total: result.totalRows,
  processed: result.skippedRows + result.failedRows,
  succeeded: 0,
  skipped: result.skippedRows,
  failed: result.failedRows,
  deliveryMode: result.deliveryMode,
  status: 'running' as const,
});

/** The delivery argument comes from the Delivery CTA, not an asynchronously updated store. */
export const buildStartImportPayload = (
  programId: string,
  rows: ParsedRow[],
  delivery: CsvImportDelivery
) => ({
  programId,
  rows: rows.map((row) => ({
    rowNumber: row.rowNumber,
    email: row.email,
    firstName: row.firstName,
    shopifyCode: row.shopifyCode,
  })),
  ...delivery,
});

/**
 * What to tell the brand when the import refuses to start.
 *
 * A 422 names the field VineJS rejected. Everything else settles on an
 * envelope `code` — a mailbox that went stale between the delivery step and the
 * POST, a program deactivated mid-wizard, a template the brand no longer owns —
 * and dropping it left the brand staring at "check your delivery settings" with
 * nothing to check. Resolve `errors.<CODE>` like every other dashboard failure,
 * fall back to the envelope's English message, and stay silent only when the
 * request never reached the API.
 */
export const describeStartFailure = (
  error: unknown,
  t: (key: string, options?: Record<string, unknown>) => string
): string | undefined => {
  const fieldError = apiValidationFieldError(error);
  if (fieldError) {
    return t('affiliateImport.errors.startFailedField', {
      field: fieldError.field,
      message: fieldError.message,
    });
  }

  const code = apiErrorCode(error);
  const translated = code ? t(`errors.${code}`, { defaultValue: '' }) : '';
  return translated || apiErrorMessage(error, '') || undefined;
};

export interface UseAffiliateImportWizardArgs {
  presetProgramId?: string;
}

export interface UseAffiliateImportWizardResult {
  isOpen: boolean;
  step: WizardStep | null;
  stepIndex: number;
  programId: string;
  programIdFromUrl: string;
  batchIdFromUrl: string;
  rows: ParsedRow[];
  batch: ImportBatchProgress | null;
  delivery: CsvImportDelivery;
  programOptions: Array<{ id: string; name: string }>;
  isStarting: boolean;
  showProgramPicker: boolean;
  setStep: (step: WizardStep | null) => void;
  setProgramIdFromUrl: (programId: string) => void;
  closeModal: () => void;
  handleParsed: (hasMissingHeaders: boolean) => void;
  handleStartImport: (deliveryOverride?: CsvImportDelivery) => Promise<void>;
  handleImportComplete: () => void;
  handleContinueInBackground: () => void;
  handleReset: () => void;
  handleImportExpired: () => void;
  handlePreviewBack: () => void;
  handleDeliveryBack: () => void;
}

export const useAffiliateImportWizard = ({
  presetProgramId,
}: UseAffiliateImportWizardArgs): UseAffiliateImportWizardResult => {
  const { t } = useTranslation();
  // The wizard owns these three URL keys as a single state object. Transitions
  // that involve more than one key must be atomic, otherwise independently
  // queued updates can leave a terminal report without its program context.
  const [importUrl, setImportUrl] = useQueryStates({
    step: parseAsStringEnum<WizardStep>(STEP_VALUES),
    program: parseAsString,
    job: parseAsString,
  });
  const step = importUrl.step;
  const programIdFromUrl = importUrl.program ?? '';
  const batchIdFromUrl = importUrl.job ?? '';

  const rows = useAffiliateCsvImportStore((s) => s.rows);
  const batch = useAffiliateCsvImportStore((s) => s.batch);
  const delivery = useAffiliateCsvImportStore((s) => s.delivery);
  const setBatch = useAffiliateCsvImportStore((s) => s.setBatch);
  const reset = useAffiliateCsvImportStore((s) => s.reset);

  const { data: programs } = useListProgram();
  const startImport = useAffiliateCsvImport();

  const isOpen = step !== null;
  // A durable ?job belongs to one program. When the URL carries a job, its own
  // ?program wins over the route preset: a report opened from another program's
  // page must not be looked up under the preset and reported as expired.
  const hasActiveJob = Boolean(batchIdFromUrl);
  const programId = hasActiveJob
    ? programIdFromUrl || presetProgramId || ''
    : (presetProgramId ?? programIdFromUrl);

  const setStep = useCallback(
    (next: WizardStep | null) => {
      void setImportUrl({ step: next });
    },
    [setImportUrl]
  );

  const setProgramIdFromUrl = useCallback(
    (next: string) => {
      void setImportUrl({ program: next });
    },
    [setImportUrl]
  );

  // Lift preset program prop into URL state so the URL stays shareable from the
  // /programs/:id/affiliates entry point. Skipped while a ?job is present, which
  // would otherwise overwrite the batch's own program.
  useEffect(() => {
    if (isOpen && !hasActiveJob && presetProgramId && programIdFromUrl !== presetProgramId) {
      void setImportUrl({ program: presetProgramId });
    }
  }, [hasActiveJob, isOpen, presetProgramId, programIdFromUrl, setImportUrl]);

  // Bounce away from steps whose payload is missing.
  useEffect(() => {
    if (step !== null && shouldRedirectToUpload(step, rows.length)) {
      void setImportUrl({ step: 'upload' });
    }
  }, [rows.length, setImportUrl, step]);

  const handleContinueInBackground = useCallback(() => {
    // Retain ?program and ?job. AffiliateImportBackgroundMonitor remains
    // mounted outside the lazy modal and polls this durable batch to completion.
    void setImportUrl({ step: null });
    toaster.create({ title: t('affiliateImport.background.continuing'), type: 'info' });
  }, [setImportUrl, t]);

  const closeModal = useCallback(() => {
    if (shouldContinueImportInBackground(step, programId, batchIdFromUrl, batch?.status)) {
      handleContinueInBackground();
      return;
    }
    void setImportUrl({ step: null, program: null, job: null });
    reset();
  }, [
    batch?.status,
    batchIdFromUrl,
    handleContinueInBackground,
    programId,
    reset,
    setImportUrl,
    step,
  ]);

  const handleParsed = useCallback(
    (hasMissingHeaders: boolean) => {
      void setImportUrl({ step: hasMissingHeaders ? 'mapping' : 'preview' });
    },
    [setImportUrl]
  );

  const handleStartImport = useCallback(
    async (deliveryOverride?: CsvImportDelivery) => {
      if (!programId) return;
      const validRows = rows.filter((r) => r.errors.length === 0);
      if (validRows.length === 0) {
        // Reachable whenever every parsed row carries an error. Returning in
        // silence made the delivery CTA look broken rather than refused.
        toaster.create({
          title: t('affiliateImport.errors.startFailed'),
          description: t('affiliateImport.errors.noValidRows'),
          type: 'error',
        });
        return;
      }

      try {
        const result = await startImport.mutateAsync(
          buildStartImportPayload(programId, rows, deliveryOverride ?? delivery)
        );

        setBatch(buildInitialImportBatch(result));
        await setImportUrl({ job: result.batchId, step: 'importing' });
      } catch (error) {
        toaster.create({
          title: t('affiliateImport.errors.startFailed'),
          description: describeStartFailure(error, t),
          type: 'error',
        });
      }
    },
    [delivery, programId, rows, setBatch, setImportUrl, startImport, t]
  );

  const handleImportComplete = useCallback(() => {
    const terminalContext =
      programId && batchIdFromUrl ? { programId, batchId: batchIdFromUrl } : null;
    if (!terminalContext) {
      void setImportUrl({ step: 'done' });
      return;
    }

    void setImportUrl({
      step: 'done',
      program: terminalContext.programId,
      job: terminalContext.batchId,
    });
  }, [batchIdFromUrl, programId, setImportUrl]);

  const handleReset = useCallback(() => {
    reset();
    void setImportUrl({ job: null, step: 'upload' });
  }, [reset, setImportUrl]);

  /** An expired batch has the same remedy as "import another file": start over. */
  const handleImportExpired = handleReset;

  const handlePreviewBack = useCallback(() => {
    void setImportUrl({ step: rows.length > 0 ? 'mapping' : 'upload' });
  }, [rows.length, setImportUrl]);

  const handleDeliveryBack = useCallback(() => {
    void setImportUrl({ step: 'preview' });
  }, [setImportUrl]);

  const programOptions = useMemo(
    () => (programs ?? []).map((p) => ({ id: p.id, name: p.name })),
    [programs]
  );

  const showProgramPicker = !presetProgramId;
  const stepIndex = step !== null ? STEP_VALUES.indexOf(step) : 0;

  return {
    isOpen,
    step,
    stepIndex,
    programId,
    programIdFromUrl,
    batchIdFromUrl,
    rows,
    batch,
    delivery,
    programOptions,
    isStarting: startImport.isPending,
    showProgramPicker,
    setStep,
    setProgramIdFromUrl,
    closeModal,
    handleParsed,
    handleStartImport,
    handleImportComplete,
    handleContinueInBackground,
    handleReset,
    handleImportExpired,
    handlePreviewBack,
    handleDeliveryBack,
  };
};
